import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Routes } from "router/config";
import { getCurrentLanguage, useTranslation } from "locales";
import { getLandingContent, LandingContent } from "./content";
import "./style.scss";

const DOWNLOADS_BASE = "https://messenger.scrile.com/downloads";

function DownloadButton({ content }: { content: LandingContent["download"] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="landing-download" ref={ref}>
      <button className="landing-btn landing-btn--download" onClick={() => setOpen((v) => !v)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        {content.label}
        <svg className={`landing-download__chevron${open ? " open" : ""}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="landing-download__menu">
          <a
            href={`${DOWNLOADS_BASE}/SMeet-arm64.dmg`}
            className="landing-download__item"
            onClick={() => setOpen(false)}
          >
            <span className="landing-download__icon">󰀵</span>
            <span>
              <strong>{content.macArm64Title}</strong>
              <span className="landing-download__hint">{content.macArm64Hint}</span>
            </span>
          </a>
          <a
            href={`${DOWNLOADS_BASE}/SMeet-x64.dmg`}
            className="landing-download__item landing-download__item--disabled"
            onClick={(e) => e.preventDefault()}
            title={content.macIntelHint}
          >
            <span className="landing-download__icon">󰻟</span>
            <span>
              <strong>{content.macIntelTitle}</strong>
              <span className="landing-download__hint">{content.macIntelHint}</span>
            </span>
          </a>
        </div>
      )}
    </div>
  );
}

/* ── Inline SVG icon components ── */

function IconMessaging() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="8" width="56" height="40" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <path d="M16 52L28 48H60" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="22" cy="28" r="2.5" fill="currentColor" />
      <circle cx="32" cy="28" r="2.5" fill="currentColor" />
      <circle cx="42" cy="28" r="2.5" fill="currentColor" />
    </svg>
  );
}

function IconVideo() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="14" width="38" height="36" rx="5" stroke="currentColor" strokeWidth="2.5" />
      <path d="M42 26L58 16V48L42 38" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <circle cx="23" cy="32" r="6" stroke="currentColor" strokeWidth="2" />
      <path d="M20 32L22 34L26 30" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconGroup() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="32" cy="18" r="8" stroke="currentColor" strokeWidth="2.5" />
      <path d="M16 50C16 41.16 23.16 34 32 34C40.84 34 48 41.16 48 50" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="50" cy="22" r="5.5" stroke="currentColor" strokeWidth="2" />
      <path d="M56 44C56 38.48 51.52 34 46 34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="14" cy="22" r="5.5" stroke="currentColor" strokeWidth="2" />
      <path d="M8 44C8 38.48 12.48 34 18 34" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconCredits() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="2.5" />
      <path d="M32 16V48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 24C24 24 26 20 32 20C38 20 40 24 40 26C40 30 32 30 32 30C32 30 24 30 24 34C24 38 28 42 32 42C36 42 40 38 40 38" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M32 4L8 16V30C8 46 18 56 32 60C46 56 56 46 56 30V16L32 4Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M22 32L28 38L42 24" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconFiles() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M8 12C8 9.79 9.79 8 12 8H28L34 14H52C54.21 14 56 15.79 56 18V50C56 52.21 54.21 54 52 54H12C9.79 54 8 52.21 8 50V12Z" stroke="currentColor" strokeWidth="2.5" />
      <path d="M24 34H40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 42H36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconEmoji() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="22" cy="26" r="3" fill="currentColor" />
      <circle cx="42" cy="26" r="3" fill="currentColor" />
      <path d="M20 40C22 44 26.69 48 32 48C37.31 48 42 44 44 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconNotification() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M32 6C24 6 16 14 16 22V36L10 46H54L48 36V22C48 14 40 6 32 6Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M26 50C26 53.31 28.69 56 32 56C35.31 56 38 53.31 38 50" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="48" cy="14" r="6" fill="currentColor" />
    </svg>
  );
}

function IconLive() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="32" cy="32" r="8" fill="currentColor" />
      <path d="M18 18C12 24 12 34 18 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M46 18C52 24 52 34 46 40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M12 10C2 20 2 38 12 48" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M52 10C62 20 62 38 52 48" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="28" cy="28" r="18" stroke="currentColor" strokeWidth="2.5" />
      <path d="M42 42L56 56" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <path d="M20 28H36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M28 20V36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="2.5" />
      <ellipse cx="32" cy="32" rx="12" ry="26" stroke="currentColor" strokeWidth="2" />
      <path d="M8 24H56" stroke="currentColor" strokeWidth="2" />
      <path d="M8 40H56" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function IconChannel() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M8 32L18 12H28L22 28H34L22 56L46 28H34L40 12H50L56 32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="2.5" />
    </svg>
  );
}

function IconLinkPreview() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="8" y="8" width="48" height="48" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <rect x="8" y="8" width="48" height="20" rx="6" fill="currentColor" opacity="0.12" />
      <path d="M16 40H48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 48H36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="44" cy="18" r="5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function IconMarkdown() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="12" width="56" height="40" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <path d="M14 40V24L22 34L30 24V40" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M42 40L42 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M36 32L42 24L48 32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconQR() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="6" y="6" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="2.5" />
      <rect x="12" y="12" width="8" height="8" fill="currentColor" />
      <rect x="38" y="6" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="2.5" />
      <rect x="44" y="12" width="8" height="8" fill="currentColor" />
      <rect x="6" y="38" width="20" height="20" rx="2" stroke="currentColor" strokeWidth="2.5" />
      <rect x="12" y="44" width="8" height="8" fill="currentColor" />
      <rect x="38" y="38" width="6" height="6" fill="currentColor" />
      <rect x="50" y="38" width="6" height="6" fill="currentColor" />
      <rect x="38" y="50" width="6" height="6" fill="currentColor" />
      <rect x="50" y="50" width="6" height="6" fill="currentColor" />
    </svg>
  );
}

function IconVoice() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="24" y="8" width="16" height="28" rx="8" stroke="currentColor" strokeWidth="2.5" />
      <path d="M16 32c0 8.837 7.163 16 16 16s16-7.163 16-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="32" y1="48" x2="32" y2="56" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="24" y1="56" x2="40" y2="56" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconReply() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M24 20V12L8 28l16 16v-8.5c12 0 20 4 24 12-2-12-8-22-24-24z" fill="currentColor" opacity="0.15" />
      <path d="M24 20V12L8 28l16 16v-8.5c12 0 20 4 24 12-2-12-8-22-24-24z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M38 28V14h2V8H24v6h2v14l-4 4v4h11v12h2V36h11v-4l-4-4z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

function IconIncomingCall() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M44 20L52 12M52 12L52 20M52 12L44 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M42.01 37.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99h-3.46C25.65 25 25 25.24 25 25.99 25 35.28 32.73 43 42.01 43c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" stroke="currentColor" strokeWidth="2.5" />
      <path d="M16 46C10 40 10 28 16 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      <path d="M10 52C2 42 2 24 10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.25" />
    </svg>
  );
}

function IconNetworkQuality() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="8" y="44" width="8" height="12" rx="2" fill="currentColor" opacity="0.4" />
      <rect x="20" y="36" width="8" height="20" rx="2" fill="currentColor" opacity="0.6" />
      <rect x="32" y="26" width="8" height="30" rx="2" fill="currentColor" opacity="0.8" />
      <rect x="44" y="16" width="8" height="40" rx="2" fill="currentColor" />
      <path d="M14 14L28 6L42 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}

function IconPiP() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="10" width="56" height="38" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <rect x="32" y="26" width="24" height="18" rx="3" fill="currentColor" opacity="0.2" stroke="currentColor" strokeWidth="2" />
      <path d="M12 56H52" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconVirtualBg() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="8" width="56" height="48" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="32" cy="28" r="10" stroke="currentColor" strokeWidth="2.5" />
      <path d="M22 44c0-5.52 4.48-10 10-10s10 4.48 10 10" stroke="currentColor" strokeWidth="2.5" />
      <path d="M4 38L20 26L32 34L44 22L60 32" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}

function IconThread() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="4" width="40" height="30" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <path d="M12 40L20 34H44" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="20" y="28" width="40" height="26" rx="6" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2" />
      <path d="M28 54L34 48H60" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="16" cy="18" r="2" fill="currentColor" />
      <circle cx="24" cy="18" r="2" fill="currentColor" />
      <circle cx="32" cy="18" r="2" fill="currentColor" />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M6 16C6 12.69 8.69 10 12 10H24L30 16H52C55.31 16 58 18.69 58 22V48C58 51.31 55.31 54 52 54H12C8.69 54 6 51.31 6 48V16Z" stroke="currentColor" strokeWidth="2.5" />
      <line x1="18" y1="30" x2="46" y2="30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      <line x1="18" y1="38" x2="38" y2="38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
      <line x1="18" y1="46" x2="30" y2="46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

function IconGlobalSearch() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="28" cy="28" r="18" stroke="currentColor" strokeWidth="2.5" />
      <path d="M42 42L56 56" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <rect x="18" y="20" width="20" height="3" rx="1.5" fill="currentColor" opacity="0.4" />
      <rect x="18" y="26" width="16" height="3" rx="1.5" fill="currentColor" opacity="0.3" />
      <rect x="18" y="32" width="12" height="3" rx="1.5" fill="currentColor" opacity="0.2" />
    </svg>
  );
}

function IconGif() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="12" width="56" height="40" rx="6" stroke="currentColor" strokeWidth="2.5" />
      <text x="32" y="38" textAnchor="middle" fill="currentColor" fontSize="18" fontWeight="800" fontFamily="sans-serif">GIF</text>
    </svg>
  );
}

function IconVideoCircle() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <circle cx="32" cy="32" r="26" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="32" cy="32" r="20" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" opacity="0.3" />
      <path d="M26 22V42L44 32Z" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

function IconTranscription() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="24" y="4" width="16" height="24" rx="8" stroke="currentColor" strokeWidth="2.5" />
      <path d="M16 28c0 8.837 7.163 16 16 16s16-7.163 16-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="8" y="50" width="48" height="3" rx="1.5" fill="currentColor" opacity="0.5" />
      <rect x="14" y="56" width="36" height="3" rx="1.5" fill="currentColor" opacity="0.3" />
    </svg>
  );
}

function IconMediaGallery() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="4" width="26" height="26" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <rect x="34" y="4" width="26" height="12" rx="4" stroke="currentColor" strokeWidth="2" />
      <rect x="34" y="20" width="26" height="10" rx="4" stroke="currentColor" strokeWidth="2" />
      <rect x="4" y="34" width="26" height="10" rx="4" stroke="currentColor" strokeWidth="2" />
      <rect x="4" y="48" width="56" height="12" rx="4" stroke="currentColor" strokeWidth="2" />
      <circle cx="17" cy="15" r="4" fill="currentColor" opacity="0.3" />
      <path d="M4 24L12 16L20 22L26 18L30 24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

function IconSavedMessages() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M44 8H20C17.79 8 16 9.79 16 12V56L32 48L48 56V12C48 9.79 46.21 8 44 8Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
      <path d="M24 24H40" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M24 32H36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconDarkMode() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8z" stroke="currentColor" strokeWidth="2.5" />
      <path d="M32 8v48c13.255 0 24-10.745 24-24S45.255 8 32 8z" fill="currentColor" opacity="0.2" />
      <circle cx="32" cy="32" r="8" stroke="currentColor" strokeWidth="2" />
      <path d="M32 18V14M32 50V46M46 32H50M14 32H18M42 22L45 19M19 45L22 42M42 42L45 45M19 19L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    </svg>
  );
}

function IconDesktopApp() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="6" width="56" height="38" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <line x1="24" y1="44" x2="40" y2="44" stroke="currentColor" strokeWidth="2.5" />
      <line x1="20" y1="52" x2="44" y2="52" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="32" y1="44" x2="32" y2="52" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="32" cy="14" r="2" fill="currentColor" opacity="0.3" />
      <rect x="14" y="22" width="36" height="14" rx="3" fill="currentColor" opacity="0.08" />
    </svg>
  );
}

function IconSwipeReply() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="12" y="18" width="44" height="28" rx="10" stroke="currentColor" strokeWidth="2.5" />
      <path d="M4 32L14 26V38Z" fill="currentColor" opacity="0.4" />
      <path d="M26 28H42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M26 36H38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
      <path d="M20 50L12 42L20 34" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 42H32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconRecording() {
  return (
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-icon">
      <rect x="4" y="10" width="56" height="38" rx="4" stroke="currentColor" strokeWidth="2.5" />
      <circle cx="50" cy="18" r="6" fill="currentColor" opacity="0.8" />
      <text x="50" y="20" textAnchor="middle" fill="white" fontSize="6" fontWeight="700" fontFamily="sans-serif">R</text>
      <rect x="16" y="22" width="20" height="16" rx="3" fill="currentColor" opacity="0.15" />
      <path d="M24 27V33L29 30Z" fill="currentColor" opacity="0.5" />
      <path d="M12 56H52" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
    </svg>
  );
}

/* ── Hero illustration (chat mockup) ── */

function HeroIllustration({ mockup }: { mockup: LandingContent["heroMockup"] }) {
  return (
    <svg viewBox="0 0 480 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-hero-illustration">
      {/* Window frame */}
      <rect x="0" y="0" width="480" height="360" rx="16" fill="var(--palette-white)" stroke="var(--palette-neutral-lighten-2)" strokeWidth="1.5" />
      <rect x="0" y="0" width="480" height="40" rx="16" fill="var(--palette-primary)" />
      <rect x="0" y="24" width="480" height="16" fill="var(--palette-primary)" />
      {/* Window dots */}
      <circle cx="20" cy="20" r="5" fill="rgba(255,255,255,0.4)" />
      <circle cx="36" cy="20" r="5" fill="rgba(255,255,255,0.4)" />
      <circle cx="52" cy="20" r="5" fill="rgba(255,255,255,0.4)" />
      <text x="240" y="25" textAnchor="middle" fill="white" fontSize="13" fontWeight="600" fontFamily="Open Sans, sans-serif">{mockup.appName}</text>

      {/* Sidebar */}
      <rect x="0" y="40" width="150" height="320" fill="var(--palette-main-bg)" />
      <line x1="150" y1="40" x2="150" y2="360" stroke="var(--palette-neutral-lighten-2)" strokeWidth="1" />

      {/* Sidebar items */}
      <rect x="12" y="56" width="126" height="44" rx="8" fill="var(--palette-primary)" opacity="0.12" />
      <circle cx="34" cy="78" r="14" fill="var(--palette-primary)" opacity="0.3" />
      <text x="34" y="82" textAnchor="middle" fill="var(--palette-primary)" fontSize="11" fontWeight="700" fontFamily="Open Sans, sans-serif">{mockup.user1Initials}</text>
      <rect x="56" y="68" width="70" height="8" rx="4" fill="var(--palette-foreground)" opacity="0.7" />
      <rect x="56" y="82" width="50" height="6" rx="3" fill="var(--palette-neutral)" opacity="0.5" />

      <circle cx="34" cy="126" r="14" fill="var(--palette-neutral)" opacity="0.15" />
      <text x="34" y="130" textAnchor="middle" fill="var(--palette-neutral)" fontSize="11" fontWeight="700" fontFamily="Open Sans, sans-serif">{mockup.user2Initials}</text>
      <rect x="56" y="116" width="60" height="8" rx="4" fill="var(--palette-foreground)" opacity="0.4" />
      <rect x="56" y="130" width="70" height="6" rx="3" fill="var(--palette-neutral)" opacity="0.3" />

      <circle cx="34" cy="174" r="14" fill="var(--palette-neutral)" opacity="0.15" />
      <text x="34" y="178" textAnchor="middle" fill="var(--palette-neutral)" fontSize="11" fontWeight="700" fontFamily="Open Sans, sans-serif">{mockup.user3Initials}</text>
      <rect x="56" y="164" width="55" height="8" rx="4" fill="var(--palette-foreground)" opacity="0.4" />
      <rect x="56" y="178" width="65" height="6" rx="3" fill="var(--palette-neutral)" opacity="0.3" />

      {/* Unread badge */}
      <circle cx="130" cy="126" r="9" fill="var(--palette-primary)" />
      <text x="130" y="130" textAnchor="middle" fill="white" fontSize="10" fontWeight="700" fontFamily="Open Sans, sans-serif">3</text>

      {/* Chat area */}
      {/* Incoming message */}
      <rect x="170" y="60" width="200" height="44" rx="12" fill="var(--palette-input-bg)" />
      <text x="182" y="78" fill="var(--palette-primary)" fontSize="10" fontWeight="700" fontFamily="Open Sans, sans-serif">{mockup.incomingFrom}</text>
      <text x="182" y="94" fill="var(--palette-foreground)" fontSize="11" fontFamily="Open Sans, sans-serif">{mockup.incomingText}</text>

      {/* Outgoing message */}
      <rect x="240" y="118" width="220" height="36" rx="12" fill="var(--palette-primary)" />
      <text x="252" y="140" fill="white" fontSize="11" fontFamily="Open Sans, sans-serif">{mockup.outgoingText}</text>

      {/* Reactions */}
      <rect x="390" y="156" width="42" height="20" rx="10" fill="var(--palette-primary)" opacity="0.12" />
      <text x="400" y="170" fontSize="12" fontFamily="Open Sans, sans-serif">&#128077; 2</text>

      {/* Incoming message with file */}
      <rect x="170" y="186" width="220" height="56" rx="12" fill="var(--palette-input-bg)" />
      <text x="182" y="204" fill="var(--palette-primary)" fontSize="10" fontWeight="700" fontFamily="Open Sans, sans-serif">{mockup.incomingFrom}</text>
      <rect x="182" y="210" width="140" height="24" rx="6" fill="var(--palette-white)" stroke="var(--palette-neutral-lighten-2)" strokeWidth="1" />
      <text x="198" y="226" fill="var(--palette-foreground)" fontSize="9" fontFamily="Open Sans, sans-serif">{mockup.fileAttachmentName}</text>

      {/* Read receipts */}
      <text x="448" y="148" fill="var(--palette-primary)" fontSize="10" fontFamily="Open Sans, sans-serif">&#10003;&#10003;</text>

      {/* Video call bar */}
      <rect x="170" y="262" width="290" height="42" rx="8" fill="var(--palette-positive)" opacity="0.12" />
      <circle cx="194" cy="283" r="10" fill="var(--palette-positive)" opacity="0.3" />
      <circle cx="194" cy="283" r="4" fill="var(--palette-positive)" />
      <text x="212" y="280" fill="var(--palette-positive)" fontSize="10" fontWeight="700" fontFamily="Open Sans, sans-serif">{mockup.callActive}</text>
      <text x="212" y="294" fill="var(--palette-positive)" fontSize="9" fontFamily="Open Sans, sans-serif" opacity="0.7">{mockup.callParticipants}</text>
      <rect x="390" y="272" width="56" height="24" rx="12" fill="var(--palette-positive)" />
      <text x="418" y="288" textAnchor="middle" fill="white" fontSize="10" fontWeight="600" fontFamily="Open Sans, sans-serif">{mockup.join}</text>

      {/* Input area */}
      <rect x="170" y="316" width="290" height="32" rx="16" fill="var(--palette-input-bg)" stroke="var(--palette-neutral-lighten-2)" strokeWidth="1" />
      <text x="186" y="336" fill="var(--palette-neutral)" fontSize="11" fontFamily="Open Sans, sans-serif">{mockup.typeMessage}</text>
      <circle cx="438" cy="332" r="12" fill="var(--palette-primary)" />
      <path d="M433 332L438 327L443 332M438 327V337" stroke="white" strokeWidth="1.5" strokeLinecap="round" transform="rotate(90, 438, 332)" />
    </svg>
  );
}

/* ── Feature icons (parallel to content arrays in content.ts) ── */

const HERO_ICONS = [
  <IconMessaging key="hero-0" />,
  <IconVideo key="hero-1" />,
  <IconGroup key="hero-2" />,
  <IconTranscription key="hero-3" />,
];

const ALL_FEATURE_ICONS = [
  <IconTranscription key="all-0" />,
  <IconTranscription key="all-1" />,
  <IconGlobalSearch key="all-2" />,
  <IconVoice key="all-3" />,
  <IconIncomingCall key="all-4" />,
  <IconEmoji key="all-5" />,
  <IconFiles key="all-6" />,
  <IconNotification key="all-7" />,
  <IconLive key="all-8" />,
  <IconVoice key="all-9" />,
  <IconGlobalSearch key="all-10" />,
  <IconReply key="all-11" />,
  <IconPin key="all-12" />,
  <IconFolder key="all-13" />,
  <IconGlobe key="all-14" />,
  <IconShield key="all-15" />,
  <IconQR key="all-16" />,
];

const NEW_FEATURE_ICONS = [
  <IconGif key="new-0" />,
  <IconVideoCircle key="new-1" />,
  <IconTranscription key="new-2" />,
  <IconVoice key="new-3" />,
  <IconTranscription key="new-4" />,
  <IconTranscription key="new-5" />,
  <IconGlobalSearch key="new-6" />,
  <IconVoice key="new-7" />,
  <IconMediaGallery key="new-8" />,
  <IconDarkMode key="new-9" />,
  <IconDesktopApp key="new-10" />,
  <IconSavedMessages key="new-11" />,
  <IconSwipeReply key="new-12" />,
  <IconThread key="new-13" />,
];

/* ── Page component ── */

function setMeta(name: string, value: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setOg(prop: string, value: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function setCanonical(url: string) {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

export default function PageLanding() {
  // Re-render when i18next changes language so the content object swaps.
  useTranslation();
  const lang = getCurrentLanguage();
  const C = getLandingContent(lang);

  useEffect(() => {
    document.title = C.meta.title;
    setMeta("description", C.meta.description);
    setOg("og:title", C.meta.title);
    setOg("og:description", C.meta.description);
    setOg("og:locale", lang === "ru" ? "ru_RU" : "en_US");
    setOg("og:url", "https://messenger.scrile.com/landing");
    setCanonical("https://messenger.scrile.com/landing");
    document.documentElement.setAttribute("lang", lang);
  }, [C, lang]);

  // Take new-features cards in the order they appear visually: 4 + 4 + 3 + 3.
  const newCards = C.newFeatures.cards;
  const newFirst4 = newCards.slice(0, 4);
  const newSecond4 = newCards.slice(4, 8);
  const newThird3 = newCards.slice(8, 11);
  const newFourth3 = newCards.slice(11, 14);
  const newFirst4Icons = NEW_FEATURE_ICONS.slice(0, 4);
  const newSecond4Icons = NEW_FEATURE_ICONS.slice(4, 8);
  const newThird3Icons = NEW_FEATURE_ICONS.slice(8, 11);
  const newFourth3Icons = NEW_FEATURE_ICONS.slice(11, 14);

  const videoMockup = C.videoSection.mockup;

  return (
    <div className="page-landing">
      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-wrap">
          <div className="landing-hero__text">
            <div className="landing-badge">{C.hero.badge}</div>
            <h1 className="landing-hero__title">
              {C.hero.titleBefore}
              <span className="landing-hero__highlight">{C.hero.titleHighlight}</span>
              {C.hero.titleAfter}
            </h1>
            <p className="landing-hero__subtitle">{C.hero.subtitle}</p>
            <div className="landing-hero__actions">
              <Link to={Routes.signup} className="landing-btn landing-btn--primary">
                {C.hero.getStarted}
              </Link>
              <Link to={Routes.login} className="landing-btn landing-btn--outline">
                {C.hero.signIn}
              </Link>
              <DownloadButton content={C.download} />
            </div>
          </div>
          <div className="landing-hero__visual">
            <HeroIllustration mockup={C.heroMockup} />
          </div>
        </div>
      </section>

      {/* ── Key Features ── */}
      <section className="landing-section">
        <div className="landing-wrap">
          <h2 className="landing-section__title">{C.heroFeaturesIntro.title}</h2>
          <p className="landing-section__subtitle">
            {C.heroFeaturesIntro.subtitle}{" "}
            <Link to={Routes.features} className="landing-link">{C.exploreAll}</Link>
          </p>
          <div className="landing-features-grid landing-features-grid--4">
            {C.heroFeatures.map((f, i) => (
              <div className="landing-feature-card" key={f.title}>
                <div className="landing-feature-card__icon">{HERO_ICONS[i]}</div>
                <h3 className="landing-feature-card__title">{f.title}</h3>
                <p className="landing-feature-card__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Messaging showcase ── */}
      <section className="landing-section landing-section--alt">
        <div className="landing-wrap landing-showcase">
          <div className="landing-showcase__text">
            <h2 className="landing-section__title landing-section__title--left">
              {C.messagingSection.title}
            </h2>
            <ul className="landing-checklist">
              {C.messagingSection.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="landing-showcase__visual">
            <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-showcase-svg">
              {/* Message bubbles */}
              <rect x="0" y="0" width="320" height="280" rx="12" fill="var(--palette-white)" />
              <rect x="16" y="16" width="180" height="36" rx="12" fill="var(--palette-input-bg)" />
              <text x="28" y="38" fill="var(--palette-foreground)" fontSize="11" fontFamily="Open Sans, sans-serif">{C.messagingSection.mockup.incoming}</text>
              {/* Reaction */}
              <rect x="16" y="56" width="36" height="18" rx="9" fill="var(--palette-primary)" opacity="0.12" />
              <text x="24" y="69" fontSize="10">&#128077; 3</text>

              <rect x="100" y="86" width="204" height="36" rx="12" fill="var(--palette-primary)" />
              <text x="112" y="108" fill="white" fontSize="11" fontFamily="Open Sans, sans-serif">{C.messagingSection.mockup.outgoing}</text>
              <text x="292" y="118" fill="var(--palette-primary)" fontSize="9">&#10003;&#10003;</text>

              {/* Edited message */}
              <rect x="16" y="134" width="220" height="50" rx="12" fill="var(--palette-input-bg)" />
              <text x="28" y="154" fill="var(--palette-foreground)" fontSize="11" fontFamily="Open Sans, sans-serif">{C.messagingSection.mockup.edited}</text>
              <rect x="28" y="162" width="40" height="14" rx="7" fill="var(--palette-neutral)" opacity="0.2" />
              <text x="34" y="173" fill="var(--palette-neutral)" fontSize="8" fontFamily="Open Sans, sans-serif">{C.messagingSection.mockup.editedLabel}</text>

              {/* Mention */}
              <rect x="60" y="198" width="244" height="36" rx="12" fill="var(--palette-primary)" />
              <text x="72" y="220" fill="white" fontSize="11" fontFamily="Open Sans, sans-serif">
                <tspan fill="rgba(255,255,255,0.7)">{C.messagingSection.mockup.mentionTag}</tspan> {C.messagingSection.mockup.mention}
              </text>

              {/* Date separator */}
              <line x1="16" y1="252" x2="130" y2="252" stroke="var(--palette-neutral-lighten-2)" strokeWidth="1" />
              <text x="160" y="256" textAnchor="middle" fill="var(--palette-neutral)" fontSize="9" fontFamily="Open Sans, sans-serif">{C.messagingSection.mockup.today}</text>
              <line x1="190" y1="252" x2="304" y2="252" stroke="var(--palette-neutral-lighten-2)" strokeWidth="1" />
            </svg>
          </div>
        </div>
      </section>

      {/* ── Video showcase ── */}
      <section className="landing-section">
        <div className="landing-wrap landing-showcase landing-showcase--reverse">
          <div className="landing-showcase__text">
            <h2 className="landing-section__title landing-section__title--left">
              {C.videoSection.title}
            </h2>
            <ul className="landing-checklist">
              {C.videoSection.items.map((item, idx) => (
                <li key={idx}>
                  {item.strongPrefix ? <strong>{item.strongPrefix}</strong> : null}
                  {item.text}
                </li>
              ))}
            </ul>
          </div>
          <div className="landing-showcase__visual">
            <svg viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg" className="landing-showcase-svg">
              <rect x="0" y="0" width="320" height="280" rx="12" fill="#1a1e24" />
              {/* Video grid */}
              <rect x="12" y="12" width="145" height="115" rx="8" fill="#2a2f38" />
              <rect x="163" y="12" width="145" height="115" rx="8" fill="#2a2f38" />
              <rect x="12" y="133" width="145" height="115" rx="8" fill="#2a2f38" />
              <rect x="163" y="133" width="145" height="115" rx="8" fill="#2a2f38" />
              {/* Avatars in cells */}
              <circle cx="84" cy="60" r="22" fill="var(--palette-primary)" opacity="0.3" />
              <text x="84" y="66" textAnchor="middle" fill="var(--palette-primary)" fontSize="16" fontWeight="700" fontFamily="Open Sans, sans-serif">{videoMockup.user1Initials}</text>
              <text x="84" y="108" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10" fontFamily="Open Sans, sans-serif">{videoMockup.user1Name}</text>
              <circle cx="236" cy="60" r="22" fill="var(--palette-positive)" opacity="0.3" />
              <text x="236" y="66" textAnchor="middle" fill="var(--palette-positive)" fontSize="16" fontWeight="700" fontFamily="Open Sans, sans-serif">{videoMockup.user2Initials}</text>
              <text x="236" y="108" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10" fontFamily="Open Sans, sans-serif">{videoMockup.user2Name}</text>
              <circle cx="84" cy="182" r="22" fill="var(--palette-warning)" opacity="0.3" />
              <text x="84" y="188" textAnchor="middle" fill="var(--palette-warning)" fontSize="16" fontWeight="700" fontFamily="Open Sans, sans-serif">{videoMockup.user3Initials}</text>
              <text x="84" y="230" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10" fontFamily="Open Sans, sans-serif">{videoMockup.user3Name}</text>
              {/* Active speaker border */}
              <rect x="163" y="133" width="145" height="115" rx="8" stroke="var(--palette-primary)" strokeWidth="2" fill="none" />
              <circle cx="236" cy="182" r="22" fill="var(--palette-negative)" opacity="0.3" />
              <text x="236" y="188" textAnchor="middle" fill="var(--palette-negative)" fontSize="16" fontWeight="700" fontFamily="Open Sans, sans-serif">{videoMockup.user4Initials}</text>
              <text x="236" y="230" textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="10" fontFamily="Open Sans, sans-serif">{videoMockup.user4Name}</text>
              {/* Controls bar */}
              <rect x="80" y="256" width="160" height="16" rx="8" fill="rgba(255,255,255,0.1)" />
              <circle cx="120" cy="264" r="5" fill="var(--palette-positive)" opacity="0.8" />
              <circle cx="145" cy="264" r="5" fill="rgba(255,255,255,0.3)" />
              <circle cx="170" cy="264" r="5" fill="rgba(255,255,255,0.3)" />
              <circle cx="200" cy="264" r="5" fill="var(--palette-negative)" opacity="0.8" />
            </svg>
          </div>
        </div>
      </section>

      {/* ── New Features showcase ── */}
      <section className="landing-section">
        <div className="landing-wrap">
          <h2 className="landing-section__title">
            <span className="landing-badge" style={{ verticalAlign: "middle", marginRight: 10 }}>{C.newFeatures.badge}</span>
            {C.newFeatures.title}
          </h2>
          <p className="landing-section__subtitle">
            {C.newFeatures.subtitle}{" "}
            <Link to={Routes.features} className="landing-link">{C.newFeatures.seeFull}</Link>
          </p>
          <div className="landing-features-grid landing-features-grid--2">
            {newFirst4.map((card, i) => (
              <div className="landing-feature-card" key={card.title}>
                <div className="landing-feature-card__icon">{newFirst4Icons[i]}</div>
                <h3 className="landing-feature-card__title">{card.title}</h3>
                <p className="landing-feature-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
          <div className="landing-features-grid landing-features-grid--2" style={{ marginTop: 16 }}>
            {newSecond4.map((card, i) => (
              <div className="landing-feature-card" key={card.title}>
                <div className="landing-feature-card__icon">{newSecond4Icons[i]}</div>
                <h3 className="landing-feature-card__title">{card.title}</h3>
                <p className="landing-feature-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
          <div className="landing-features-grid landing-features-grid--3" style={{ marginTop: 16 }}>
            {newThird3.map((card, i) => (
              <div className="landing-feature-card" key={card.title}>
                <div className="landing-feature-card__icon">{newThird3Icons[i]}</div>
                <h3 className="landing-feature-card__title">{card.title}</h3>
                <p className="landing-feature-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
          <div className="landing-features-grid landing-features-grid--3" style={{ marginTop: 16 }}>
            {newFourth3.map((card, i) => (
              <div className="landing-feature-card" key={card.title}>
                <div className="landing-feature-card__icon">{newFourth3Icons[i]}</div>
                <h3 className="landing-feature-card__title">{card.title}</h3>
                <p className="landing-feature-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── All features grid ── */}
      <section className="landing-section landing-section--alt">
        <div className="landing-wrap">
          <h2 className="landing-section__title">{C.allFeatures.title}</h2>
          <p className="landing-section__subtitle">
            {C.allFeatures.subtitle}{" "}
            <Link to={Routes.features} className="landing-link">{C.allFeatures.seeDetails}</Link>
          </p>
          <div className="landing-features-grid landing-features-grid--4">
            {C.allFeatures.items.map((f, i) => (
              <div className="landing-feature-card landing-feature-card--compact" key={f.title}>
                <div className="landing-feature-card__icon landing-feature-card__icon--sm">
                  {ALL_FEATURE_ICONS[i]}
                </div>
                <h3 className="landing-feature-card__title">{f.title}</h3>
                <p className="landing-feature-card__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why teams choose SMeet ── */}
      <section className="landing-section">
        <div className="landing-wrap">
          <h2 className="landing-section__title">{C.why.title}</h2>
          <p className="landing-section__subtitle">{C.why.subtitle}</p>
          <div className="landing-tech-grid">
            {C.why.highlights.map((t) => (
              <div className="landing-tech-item" key={t.label}>
                <div className="landing-tech-item__label">{t.label}</div>
                <div className="landing-tech-item__value">{t.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="landing-cta">
        <div className="landing-wrap">
          <h2 className="landing-cta__title">{C.cta.title}</h2>
          <p className="landing-cta__subtitle">{C.cta.subtitle}</p>
          <div className="landing-hero__actions">
            <Link to={Routes.signup} className="landing-btn landing-btn--white">
              {C.cta.getStarted}
            </Link>
            <a href="mailto:contact@scrile.com" className="landing-btn landing-btn--ghost">
              {C.cta.contactSales}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
